// Import HttpErrorResponse if you need to change the way Sentry handles api errors
import * as Sentry from "@sentry/angular";
import { RewriteFrames } from "@sentry/integrations";
import { Integrations } from "@sentry/tracing";
import { basename, relative } from "@sentry/utils";
import { environment } from "../../../environments/environment";

export class SentryLoader {
    public initLogging(baseHref: string, hasRoutes?: boolean) {
        let tracesSampleRate = 1.0;

        if (environment.production) {
            tracesSampleRate = 0.2;
        }

        const root = document.querySelector("base")?.href || window.location.origin;

        const isSubdirectory = root !== window.location.origin;
        const integrations: Sentry.BrowserOptions["integrations"] = [
            new RewriteFrames({
                root: baseHref,
                iteratee: (frame) => {
                    /* Start Default Iteratee */
                    if (!frame.filename) {
                        return frame;
                    }
                    const isWindowsFrame = /^[A-Z]:\\/.test(frame.filename);
                    const startsWithSlash = /^\//.test(frame.filename);
                    if (isWindowsFrame || startsWithSlash) {
                        const filename = isWindowsFrame
                            ? frame.filename
                                  .replace(/^[A-Z]:/, "") // remove Windows-style prefix
                                  .replace(/\\/g, "/") // replace all `\\` instances with `/`
                            : frame.filename;
                        const base = baseHref ? relative(baseHref, filename) : basename(filename);
                        frame.filename = base;
                    }
                    /* End Default Iteratee */

                    // Strip base href from filename if in a subdirectory
                    if (isSubdirectory) {
                        frame.filename = frame.filename.replace(root, "/");
                    }
                    return frame;
                }
            })
        ];

        if (hasRoutes) {
            integrations.push(
                new Integrations.BrowserTracing({
                    routingInstrumentation: Sentry.routingInstrumentation
                })
            );
        }

        const browserOptions: Sentry.BrowserOptions = {
            dsn: "https://c145ef21edf54dcb842ef25d091d455a@o1073115.ingest.sentry.io/4504323051814912",
            integrations,
            environment: environment.environmentName,
            release: `abbvie-epi-screening-tool@${environment.version}+${environment.buildNumber}`,
            beforeSend: (event: Sentry.Event, hint?: any) => {
                if (window.location.hostname === "localhost" && !environment.enableLocalSentryCapture) {
                    return null;
                }

                return event;
            },
            // Set tracesSampleRate to 1.0 to capture 100%
            // of transactions for performance monitoring.
            // tracesSampleRate: tracesSampleRate
            tracesSampleRate: tracesSampleRate,

            ignoreErrors: [
                // Random plugins/extensions
                "top.GLOBALS",
                "originalCreateNotification",
                "canvas.contentDocument",
                "MyApp_RemoveAllHighlights",
                "http://tt.epicplay.com",
                "Can't find variable: ZiteReader",
                "jigsaw is not defined",
                "ComboSearch is not defined",
                "http://loading.retry.widdit.com/",
                "atomicFindClose",
                // Facebook borked
                "fb_xd_fragment",
                // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
                // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
                "bmi_SafeAddOnload",
                "EBCallBackMessageReceived",
                // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
                "conduitPage",
                // Generic error code from errors outside the security sandbox
                // You can delete this if using raven.js > 1.0, which ignores these automatically.
                "Script error.",
                // Avast extension error
                "_avast_submit"
            ],
            denyUrls: [
                // Google Adsense
                /pagead\/js/i,
                // Facebook flakiness
                /graph\.facebook\.com/i,
                // Facebook blocked
                /connect\.facebook\.net\/en_US\/all\.js/i,
                // Woopra flakiness
                /eatdifferent\.com\.woopra-ns\.com/i,
                /static\.woopra\.com\/js\/woopra\.js/i,
                // Chrome extensions
                /extensions\//i,
                /^chrome:\/\//i,
                // Other plugins
                /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
                /webappstoolbarba\.texthelp\.com\//i,
                /metrics\.itunes\.apple\.com\.edgesuite\.net\//i
            ]
        };

        Sentry.init(browserOptions);
    }
}
